import {
  ChangeDetectionStrategy,
  Component,
  inject,
  viewChild,
  ViewContainerRef,
  signal,
  effect,
} from '@angular/core';
import { MatButton } from '@angular/material/button';
import { AlertService } from '@shared/services';
import { MatCard, MatCardContent, MatCardHeader, MatCardTitle } from '@angular/material/card';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { FormsModule } from '@angular/forms';

@Component({
  selector: 'app-cp-custom-position-notification',
  standalone: true,
  imports: [
    MatButton,
    MatCard,
    MatCardHeader,
    MatCardContent,
    MatCardTitle,
    MatFormField,
    MatInput,
    FormsModule,
    MatLabel,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="flex gap-4 mb-4">
      <mat-form-field>
        <mat-label>Alert Title</mat-label>
        <input
          matInput
          [ngModel]="title()"
          (ngModelChange)="title.set($event)"
          placeholder="Enter alert title"
        />
      </mat-form-field>
      <mat-form-field>
        <mat-label>Alert Message</mat-label>
        <input
          matInput
          [ngModel]="message()"
          (ngModelChange)="message.set($event)"
          placeholder="Enter alert message"
        />
      </mat-form-field>
    </div>
    <div class="flex gap-2 mb-4">
      <button mat-raised-button color="primary" (click)="showSuccess()">Success Alert</button>
      <button mat-raised-button color="accent" (click)="showInfo()">Info Alert</button>
      <button mat-raised-button color="warn" (click)="showWarning()">Warning Alert</button>
      <button mat-raised-button (click)="showError()">Error Alert</button>
      <button mat-raised-button color="basic" (click)="dismissAll()">Dismiss All</button>
    </div>
    <mat-card class="example-card" appearance="outlined">
      <mat-card-header>
        <mat-card-title>Alert Container</mat-card-title>
      </mat-card-header>
      <mat-card-content>
        <mat-card class="alerts-container">
          <div class="alerts-scroll-container">
            <ng-template #alertContainer></ng-template>
          </div>
        </mat-card>
      </mat-card-content>
    </mat-card>
  `,
  styles: `
    :host {
      display: block;
      padding: 1rem;
    }

    .example-card {
      max-width: 800px;
      margin: 0 auto;
    }

    mat-form-field {
      width: 100%;
    }

    .alerts-container {
      height: 24rem; /* h-96 equivalent */
      position: relative;
      padding: 1rem;
    }

    .alerts-scroll-container {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      overflow-y: auto;
      padding: 1rem; /* Customize scrollbar */
      &::-webkit-scrollbar {
        width: 8px;
      }

      &::-webkit-scrollbar-track {
        background: #f1f1f1;
        border-radius: 4px;
      }

      &::-webkit-scrollbar-thumb {
        background: #888;
        border-radius: 4px;

        &:hover {
          background: #555;
        }
      }
    }

    /* For Firefox */
    .alerts-scroll-container {
      scrollbar-width: thin;
      scrollbar-color: #888 #f1f1f1;
    }
  `,
})
export class CpCustomPositionNotificationComponent {
  private alertService = inject(AlertService);
  alertContainer = viewChild.required('alertContainer', { read: ViewContainerRef });
  title = signal('Success!');
  message = signal('The operation was performed successfully.');

  /**   *   */ constructor() {
    effect(() => {
      this.alertService.container = this.alertContainer();
    });
  }

  private getAlertConfig() {
    return { title: this.title(), message: this.message() };
  }

  showSuccess() {
    this.alertService.success(this.getAlertConfig());
  }

  showError() {
    this.alertService.error(this.getAlertConfig());
  }

  showWarning() {
    this.alertService.warning(this.getAlertConfig());
  }

  showInfo() {
    this.alertService.info(this.getAlertConfig());
  }

  dismissAll() {
    this.alertService.dismissAll(this.alertContainer());
  }
}
